import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatTanggal, formatRupiah, capitalizeWords } from "../../../Utils";

const generateHarianAngsuran2PDF = (data, tglmulai, tglakhir) => {
  const actualData = Array.isArray(data) ? data : [];
  if (actualData.length === 0) {
    alert("Tidak ada data untuk dibuat laporan.");
    return;
  }

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();

  const loadImageAsDataURL = (url) => {
    return fetch(url)
      .then((res) => res.blob())
      .then(
        (blob) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
      );
  };

  // load only the user's custom logo (place file at public/koperasihitamputih.png)
  const customLogoUrl = window.location.origin + "/koperasihitamputih.png";

  const drawHeader = (logoDataUrl) => {
    const logoX = 10;
    const logoY = 10;
    const logoSize = 15;
    const textX = logoX + logoSize + 2;

    if (logoDataUrl) {
      try {
        const imgType = (typeof logoDataUrl === 'string' && logoDataUrl.startsWith('data:image/png')) ? 'PNG' : 'JPEG';
        doc.addImage(logoDataUrl, imgType, logoX, logoY, logoSize, logoSize);
      } catch (e) {
        console.warn("Gagal menambahkan logo ke PDF:", e);
      }
    }

    const textY = logoY + logoSize / 2 - 1;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("KPRI SRI REJEKI", textX, textY, { align: "left" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("Jl. Donomulyo No. 164 Kab.Malang", textX, textY + 5, { align: "left" });

    const lineY = logoY + logoSize + 4;
    doc.setLineWidth(0.3);
    doc.line(8, lineY, pageWidth - 8, lineY);

    const reportY = lineY + 6;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Laporan Harian Simpanan Angsuran 2", pageWidth / 2, reportY, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(
      `Tanggal : ${formatTanggal(tglmulai)} s/d ${formatTanggal(tglakhir)}`,
      pageWidth / 2,
      reportY + 5,
      { align: "center" }
    );

    return reportY + 9;
  };

  loadImageAsDataURL(customLogoUrl)
    .then((logoDataUrl) => {
      const startY = drawHeader(logoDataUrl);
      renderTableAndExport(startY);
    })
    .catch((err) => {
      console.warn("Logo custom tidak ditemukan atau gagal dimuat, melanjutkan tanpa logo:", err);
      const startY = drawHeader();
      renderTableAndExport(startY);
    });

  const headers = [[
    { content: "No.", styles: { halign: "right" } },
    { content: "Tanggal", styles: { halign: "center" } },
    { content: "No.Transak", styles: { halign: "center" } },
    { content: "No.Anggota", styles: { halign: "center" } },
    { content: "Nama", styles: { halign: "left" } },    
    { content: "Angsuran", styles: { halign: "right" } },
    { content: "Jasa", styles: { halign: "right" } },
    { content: "Germen", styles: { halign: "right" } },
  ]];

  let totalAngsuran = 0;
  let totalJasa = 0;
  let totalGermen = 0;

  const tableData = actualData.map((item, index) => {    
    totalAngsuran += parseFloat(item.angsuran) || 0;
    totalJasa += parseFloat(item.jasa) || 0;
    totalGermen += parseFloat(item.germen) || 0;

    return [
      { content: index + 1, styles: { halign: "right" } },
      { content: formatTanggal(item.tanggal) ?? "-", styles: { halign: "center" } },
      { content: item.notransaksi ?? "-", styles: { halign: "center" } },
      { content: item.noanggota ?? "-", styles: { halign: "center" } },
      { content: capitalizeWords(item.nama) || "-", styles: { halign: "left", overflow: "linebreak" } },
      { content: formatRupiah(item.angsuran), styles: { halign: "right" } },
      { content: formatRupiah(item.jasa), styles: { halign: "right" } },
      { content: formatRupiah(item.germen), styles: { halign: "right" } },      
    ];
  });

  tableData.push([
    {
      content: "Total Keseluruhan",
      colSpan: 5,
      styles: {
        fontStyle: "bold",
        halign: "center",
        fillColor: [230, 230, 230],
      },
    },    
    {
      content: formatRupiah(totalAngsuran),
      styles: {
        fontStyle: "bold",
        halign: "right",
        fillColor: [230, 230, 230],
      },
    },
    {
      content: formatRupiah(totalJasa),
      styles: {
        fontStyle: "bold",
        halign: "right",
        fillColor: [230, 230, 230],
      },
    },
    {
      content: formatRupiah(totalGermen),
      styles: {
        fontStyle: "bold",
        halign: "right",
        fillColor: [230, 230, 230],
      },
    },
  ]);

  const renderTableAndExport = (startYParam) => {
    autoTable(doc, {
      head: headers,
      body: tableData,
      startY: startYParam,
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 1.5 },
      headStyles: {
        halign: "center",
        valign: "middle",
        fontSize: 8,
        cellPadding: 2,
        fillColor: [0, 191, 165],
        textColor: 255,
      },
      showHead: "everyPage",
      margin: { left: 8, right: 8, top: 20, bottom: 10 },
      tableWidth: "wrap",
      styles: { fontSize: 8, cellPadding: 1.5, overflow: "linebreak" },
      columnStyles: {
        0: { cellWidth: 10, halign: "right" },
        1: { cellWidth: 20, halign: "center" },
        2: { cellWidth: 20, halign: "center" },
        3: { cellWidth: 25, halign: "center" },
        4: { cellWidth: 40, halign: "left", overflow: "linebreak" },
        5: { cellWidth: 24, halign: "right" },
        6: { cellWidth: 24, halign: "right" },
        7: { cellWidth: 24, halign: "right" },
        8: { cellWidth: 24, halign: "right" },
      },
      didDrawPage: (data) => {
        let str = 'Page ' + doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.text(str, data.settings.margin.left, doc.internal.pageSize.height - 10);
      },
    });

    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, "_blank");
  };
};

export default generateHarianAngsuran2PDF;
